import PlanDetectionModel             from '@models/map/planImport/PlanDetectionModel';
import PlanDetectionStatusModel       from '@models/map/planImport/PlanDetectionStatusModel';
import userMercureListener            from '@/services/userMercureListener';
import type { MessageTemplate }       from '@/services/MercureListener';
import type { StatusReference }       from '@models/map/planImport/PlanAnalysisModel';
import type { PlanDetectionProgress } from '@/TokamapEditor/planImport/planImportTypes';
import type { PlanDetectionResult }   from '@/TokamapEditor/planImport/planImportTypes';
import type { PlanDetectionStep }     from '@/TokamapEditor/planImport/planImportTypes';

export const PLAN_DETECTION_STEP_LABELS: Record<PlanDetectionStep, string> = {
	overview: 'Lecture du plan',
	detection: 'Détection des calques',
	shapes: 'Extraction des formes',
	doubts: 'Recherche des points à vérifier',
};

const FAST_POLL_MS = 1000;
const SLOW_POLL_MS = 5000;
const FAST_POLLS = 5;

export type PlanDetectionMessage =
	| { kind: 'progress'; progress: PlanDetectionProgress }
	| { kind: 'finished' };

export function readPlanDetectionMessage(message: MessageTemplate, planDetectionId: number): PlanDetectionMessage | null {
	if (message?.data?.planDetectionId !== planDetectionId) return null;

	if ('plan_detection.progress' === message.kind) {
		const { step, current, total } = message.data as PlanDetectionProgress;

		return step ? { kind: 'progress', progress: { step, current, total } } : null;
	}

	if ('plan_detection.done' === message.kind || 'plan_detection.failed' === message.kind) {
		return { kind: 'finished' };
	}

	return null;
}

export async function waitForPlanDetection(
	planDetectionId: number,
	options: { onProgress?: (progress: PlanDetectionProgress) => void; signal?: AbortSignal } = {},
): Promise<PlanDetectionResult> {
	let wake: () => void = () => undefined;

	const listener = (message: MessageTemplate) => {
		const read = readPlanDetectionMessage(message, planDetectionId);
		if (!read) return;

		if ('progress' === read.kind) options.onProgress?.(read.progress);
		else wake();
	};

	userMercureListener.onAny(listener);

	try {
		for (let attempt = 0; ; attempt++) {
			if (options.signal?.aborted) throw new DOMException('Détection abandonnée.', 'AbortError');

			const detection = new PlanDetectionModel();
			await detection.setId(planDetectionId).fetch();

			const status = await statusReference(detection.planDetectionStatus);

			if ('DONE' === status) return detection.result as unknown as PlanDetectionResult;
			if ('ERROR' === status) throw new Error(detection.errorMessage || 'La détection des formes a échoué.');

			await new Promise<void>(resolve => {
				const timer = setTimeout(resolve, attempt < FAST_POLLS ? FAST_POLL_MS : SLOW_POLL_MS);
				wake = () => {
					clearTimeout(timer);
					resolve();
				};
			});
		}
	} finally {
		userMercureListener.offAny(listener);
	}
}

async function statusReference(status: StatusReference | string | undefined): Promise<string | undefined> {
	if (!status) return undefined;
	if ('string' !== typeof status) return status.reference;

	const id = status.split('/').pop();
	if (!id) return undefined;

	const model = new PlanDetectionStatusModel();
	await model.setId(id).fetch();

	return model.reference;
}
